// Native & external imports
import { EventEmitter, Injectable, inject } from "@angular/core";
import {
  ApiCallsService,
  CmpAuthService,
  EnvSetupObject,
} from "icamppro-shared-components";
import { isStorageFallbackActive } from '@icamppro-customer-portal/shared/cmp-base';
// Services
import { LocalStorageService } from "../../src/services/local-storage.service";
// Objects
import { HttpResponseObject } from "@iclassproinc/icamppro-shared-components";
import { CmpAlertModalObject } from "../../src/objects/cmp-alert.object";
import { CmpFamilyMemberObject } from "../../src/objects/cmp-family-member.object";

import { AppRouterService } from "../routing/routes-service";
import { Subject, firstValueFrom } from "rxjs";
import { CMP_ENV_SETUP } from "../app/cmp-env-setup.injection-token";

@Injectable({
  providedIn: "root",
})
export class LayoutService {
  private apiService = inject(ApiCallsService);
  private routerService = inject(AppRouterService);
  private localStorageService = inject(LocalStorageService);
  private authService = inject(CmpAuthService);
  cmpEnv = inject<EnvSetupObject>(CMP_ENV_SETUP);

  /**
   * True when native localStorage was blocked at startup and an in-memory fallback was
   * installed. Evaluated once (the flag is fixed before bootstrap and never changes) so
   * the template binds a plain field rather than calling a getter each change-detection
   * cycle. Used to show a warning banner, since stored data (auth token, cart, etc.) will
   * not survive a page reload in this state.
   */
  public readonly storageFallbackActive = isStorageFallbackActive();


  public portalSettings: {[index: string]: string} = {};
  public businessInfoSettings: {[index: string]: string} = {};
  public termsAndConditionSettings: {[index: string]: string} = {};
  public privacyPolicySettings: {[index: string]: string} = {};
  public sEOSettings: {[index: string]: string} = {};
  public isCustomDomain: {[index: string]: boolean} = {};
  public accountOverviewModal = false;
  public createAccountPage = false;

  public showAlertModal = false;
  public showModal: { [index: string]: boolean } = {
    alertModal: false,
    warningModal: false,
    deleteModal: false,
    profilePhoto: false,
  };
  public familyId = 0;
  public alertModalObjects: Array<CmpAlertModalObject> = [];
  public alertModalObject = new CmpAlertModalObject();
  public loggedIn = false;
  public userDetail: CmpFamilyMemberObject | null = null;
  public hideMobileNavBar = false;
  public containerClass = "container-small";
  public hideCartIcon = false;
  public shadowView = false;
  public removeAlertClass = true;
  public accessUrl = "";
  // Confirmation Modal
  public confirmModal = false;
  public emitConfirmModalResponse = new EventEmitter<void>();
  public loadingData = false;
  public noNavBar = false;
  public showTermsModal = false;
  public termsAndCondition: { [index: string]: string | boolean } = {};
  public companyName = "";
  public logOutStatus = false;
  public hasActionBarClass = false;
  public innerWidth = 0;
  public noBackground = false;
  public profilePic = new EventEmitter();
  public tenantCssLoaded = false;
  public shadowUserName = "Admin";
  public showVerticalCenter = false;
  public updateContactInfoPromptStatus: null | boolean = null;
  public staffSettingsObject: { [index: string]: string } = {};
  public isAccountCreationLoader = false;
  constructor() {
    this.checkWhetherIsLogin();
    this.innerWidth = window.innerWidth;
  }

  private refreshSubject = new Subject<void>();

  public get needToRefreshObservable() {
    return this.refreshSubject.asObservable();
  }

  public triggerNeedsRefresh() {
    this.refreshSubject.next();
  }

  /**
   * @method loadPortalSettings
   * @param forceLoad boolean
   * @description this method fetches portal Settings (logo, company name, color, etc)
   */
  public async loadPortalSettings(
    forceLoad = false,
  ): Promise<{ [index: string]: string }> {
    if (Object.keys(this.portalSettings).length > 0 && !forceLoad) {
      return this.portalSettings;
    } else {
      const url = this.routerService.backendApiUrl.portalSettings;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (
        response.data &&
        !response.hasErrors &&
        Object.keys(response.data).length > 0
      ) {
        this.portalSettings = response.data;
      }
      const backGroundAndPlaceHolderUrl =
        this.routerService.backendApiUrl.backgroundAndPlaceHolderSettings;
      const bGAndPlaceHolderResponse: HttpResponseObject<any> =
        await firstValueFrom(this.apiService.get(backGroundAndPlaceHolderUrl));
      if (
        bGAndPlaceHolderResponse &&
        bGAndPlaceHolderResponse.data &&
        !bGAndPlaceHolderResponse.hasErrors &&
        Object.keys(bGAndPlaceHolderResponse.data).length > 0
      ) {
        this.portalSettings = {
          ...this.portalSettings,
          ...bGAndPlaceHolderResponse.data,
        };
      }
      return this.portalSettings;
    }
  }

  /**
   * @method loadBusinessInfoSettings
   * @param forceLoad boolean
   * @description this method fetches portal Settings (logo, company name, color, etc)
   */
  public async loadBusinessInfoSettings(
    forceLoad = false,
  ): Promise<{ [index: string]: string }> {
    if (Object.keys(this.businessInfoSettings).length > 0 && !forceLoad) {
      return this.businessInfoSettings;
    } else {
      const url = this.routerService.backendApiUrl.businessInfoSettings;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (
        response &&
        !response.hasErrors &&
        response.data &&
        Object.keys(response.data).length > 0
      ) {
        this.businessInfoSettings = response.data;
      }
      return this.businessInfoSettings;
    }
  }

  /**
   * @method getTermsAndConditions
   * @param forceLoad boolean
   * @description this method fetches portal Settings (logo, company name, color, etc)
   */
  public async getTermsAndConditions(
    forceLoad = false,
  ): Promise<{ [index: string]: string }> {
    if (
      this.termsAndConditionSettings &&
      this.termsAndConditionSettings["valueSet"]
    ) {
      return this.termsAndConditionSettings;
    } else {
      const url = this.routerService.backendApiUrl.termsAndConditionSettings;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (response && !response.hasErrors && response.data) {
        this.termsAndConditionSettings = response.data;
        this.termsAndConditionSettings["valueSet"] = "set";
      }
      return this.termsAndConditionSettings;
    }
  }

  /**
   * @method getPrivacyPolicy
   * @param forceLoad boolean
   * @description this method fetches portal Settings (logo, company name, color, etc)
   */
  public async getPrivacyPolicy(
    forceLoad = false,
  ): Promise<{ [index: string]: string }> {
    if (this.privacyPolicySettings && this.privacyPolicySettings["valueSet"]) {
      return this.privacyPolicySettings;
    } else {
      const url = this.routerService.backendApiUrl.privacyPolicySettings;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (response && !response.hasErrors && response.data) {
        this.privacyPolicySettings = response.data;
        this.privacyPolicySettings["valueSet"] = "set";
      }
      return this.privacyPolicySettings;
    }
  }

  /**
   * @method checkWhetherIsLogin
   * @description Check whether the user is logged in or not based on local storage value
   */
  public checkWhetherIsLogin() {
    this.authService.getToken().subscribe((token) => {
      const userLocalStorage = this.localStorageService.getItem("consumer");
      this.loggedIn = !(!token || !userLocalStorage);
      if (this.loggedIn) {
        this.setUserDetail();
      }
    });
  }

  /**
   * @method setUserDetail
   * @description Set user detail in local variable from local storage for global usage
   */
  public setUserDetail() {
    const userLocalStorage = this.localStorageService.getItem("consumer");
    if (userLocalStorage) {
      this.userDetail = userLocalStorage ? JSON.parse(userLocalStorage) : null;
      this.familyId = this.userDetail ? this.userDetail.familyId : 0;
      if (this.updateContactInfoPromptStatus) {
        this.isShadowAccount();
        if (this.shadowView) {
          this.updateContactInfoPromptStatus = false;
        }
      }
    }
  }

  /**
   * @method isShadowAccount
   * @description Return whether its a shadow session based on local storage setting
   */
  public isShadowAccount() {
    const shadowUserId = sessionStorage.getItem("CMP_CONSUMER_SHADOW_ID") || 0;
    if (+shadowUserId > 0) {
      this.shadowView = true;
    } else {
      this.shadowView = false;
    }
  }

  /**
   * @method removeHasAlertClass
   * @param remove bool
   * @returns void
   */
  public removeHasAlertClass(remove: boolean) {
    this.removeAlertClass = remove;
  }

  /**
   * @method getStaffModuleSettings
   * @description return the StaffModuleSettings
   */
  public async getStaffModuleSettings() {
    if (Object.keys(this.staffSettingsObject).length > 0) {
      return this.staffSettingsObject;
    } else {
      const url = this.routerService.backendApiUrl.staffSettings;
      const staffSettings: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (staffSettings && !staffSettings.hasErrors && staffSettings.data) {
        if (staffSettings.data.textarea) {
          let termsString = staffSettings.data.textarea;
          termsString = termsString.replace(/<p/g, '<p class="no-margin-all"');
          if (termsString.indexOf("<a href=")) {
            const regExpForATag = new RegExp("<a", "g");
            staffSettings.data.textarea = termsString.replace(
              regExpForATag,
              `<a class="text-link" target="_blank" `,
            );
          }
        }
        this.staffSettingsObject = staffSettings.data;
      }
      return this.staffSettingsObject;
    }
  }

  /**
   * @method loadSEOSettings
   * @description this method fetches seo settings
   */
  public async loadSEOSettings(): Promise<{ [index: string]: string }> {
    if (Object.keys(this.sEOSettings).length > 0) {
      return this.sEOSettings;
    } else {
      const url = this.routerService.backendApiUrl.seo;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (
        response &&
        !response.hasErrors &&
        response.data &&
        Object.keys(response.data).length > 0
      ) {
        this.sEOSettings = response.data;
      }
      return this.sEOSettings;
    }
  }

  /**
   * @method checkCustomDomain
   * @description check if the portal is using custom domain
   */
  public async checkCustomDomain(): Promise<{ [index: string]: boolean }> {
    if (Object.keys(this.isCustomDomain).length > 0) {
      return this.isCustomDomain;
    } else {
      const url = this.routerService.backendApiUrl.checkCustomDomain;
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(url),
      );
      if (
        response &&
        !response.hasErrors &&
        response.data &&
        Object.keys(response.data).length > 0
      ) {
        this.isCustomDomain = response.data;
      }
      return this.isCustomDomain;
    }
  }

  /**
   * @method logOut
   * @description Clear the localStorage on logging out the user
   * @returns void
   */
  public async logOut(doRedirect: boolean = true) {
    this.logOutStatus = true;
    this.accessUrl = "/";
    const rememberCamper = { email: "", firstName: "" };
    if (this.userDetail) {
      rememberCamper["email"] = this.userDetail.email;
      rememberCamper["firstName"] = this.userDetail.firstName;
    }
    this.authService.logOut();
    this.loggedIn = false;
    this.localStorageService.setItem(
      "remember_consumer",
      JSON.stringify(rememberCamper),
    );
    if (doRedirect) {
      const tenantInfo = await this.cmpEnv.getTenantInfo();
      window.location.href = tenantInfo.gatewayPortalUrl.startsWith("http")
        ? tenantInfo.gatewayPortalUrl
        : `https://${tenantInfo.gatewayPortalUrl}`;
    }
  }

  /**
   * @method checkForStaffEnabled
   * @description check if staff portal is enabled
   * @returns object|null
   */
  public async checkForStaffEnabled() {
    const response: HttpResponseObject<any> = await firstValueFrom(
      this.apiService.get(
        `${this.routerService.backendApiUrl.staffPortalAvailability}`,
      ),
    );
    if (
      response &&
      !response.hasErrors &&
      response.data &&
      Object.keys(response.data).length > 0
    ) {
      return response.data;
    }
    return null;
  }

  /**
   * @method redirectToCamper
   * @description redirect To Camper portal
   * @returns void
   */
  public async redirectToCamper() {
    const tenantInfo = await this.cmpEnv.getTenantInfo();
    window.location.href = tenantInfo.camperPortalUrl.startsWith("http")
      ? `${tenantInfo.camperPortalUrl}/dashboard`
      : `https://${tenantInfo.camperPortalUrl}/dashboard`;
  }

  /**
   * @method redirectToStaff
   * @description redirect To staff portal
   * @returns void
   */
  public async redirectToStaff() {
    const tenantInfo = await this.cmpEnv.getTenantInfo();
    window.location.href = tenantInfo.staffPortalUrl.startsWith("http")
      ? `${tenantInfo.staffPortalUrl}/dashboard`
      : `https://${tenantInfo.staffPortalUrl}/dashboard`;
  }
}
